import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles,
  Printer,
  Lightbulb,
  ClipboardList,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Layers,
  Edit,
  Save,
  X,
} from 'lucide-react';
import { patients, treatmentPlans } from '../services/api';
import { treatmentPlanComponents } from './treatmentPlanRenderers';
import TreatmentPlanEditor from './TreatmentPlanEditor';
import TreatmentPlanSignoff from './TreatmentPlanSignoff';
import { parseMarkdownSections, reconstructMarkdown } from '../utils/markdownSections';

const TreatmentPlanTab = ({ patientId, assessmentData }) => {
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Editing state
  const [isEditMode, setIsEditMode] = useState(false);
  const [sections, setSections] = useState([]);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [originalContent, setOriginalContent] = useState(null);
  const [saving, setSaving] = useState(false);

  // Signoff save state
  const signoffSaveTimer = React.useRef(null);
  const [signoffSaveStatus, setSignoffSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  // Derive current plan from selection
  const selectedPlan = allPlans[selectedPlanIndex] || null;
  const treatmentPlan = selectedPlan?.content || null;
  const treatmentPlanGeneratedAt = selectedPlan?.createdAt || null;

  // Track unsaved changes
  const hasUnsavedChanges = isEditMode && originalContent !== null && reconstructMarkdown(sections) !== originalContent;

  // Fetch patient data
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        const response = await patients.getById(patientId);
        setPatientData(response.data);
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError('Erro ao carregar dados do paciente');
      }
    };
    if (patientId) fetchPatientData();
  }, [patientId]);

  // Fetch all treatment plan versions
  const fetchAllPlans = useCallback(async () => {
    if (!assessmentData?.id) return;
    try {
      const response = await treatmentPlans.getByAssessment(assessmentData.id);
      const plans = response.data.data || [];
      setAllPlans(plans);
      if (plans.length > 0) {
        setSelectedPlanIndex(0);
        setSpecialInstructions(plans[0].specialInstructions || '');
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching treatment plans:', err);
      }
    }
  }, [assessmentData?.id]);

  useEffect(() => {
    fetchAllPlans();
  }, [fetchAllPlans]);

  // Simulated progress during generation
  useEffect(() => {
    if (!generating) { setGenerationProgress(0); return; }
    setGenerationProgress(5);
    const interval = setInterval(() => {
      setGenerationProgress((prev) => (prev >= 90 ? prev : prev + Math.random() * 8));
    }, 1500);
    return () => clearInterval(interval);
  }, [generating]);

  // --- Version selection ---
  const handleSelectPlan = (index) => {
    if (isEditMode && hasUnsavedChanges) {
      if (!window.confirm('Você tem alterações não salvas. Deseja descartá-las e trocar de versão?')) return;
    }
    setIsEditMode(false);
    setEditingSectionIndex(null);
    setOriginalContent(null);
    setSelectedPlanIndex(index);
    setSpecialInstructions(allPlans[index]?.specialInstructions || '');
  };

  // --- Generation ---
  const handleGenerateTreatmentPlan = async (overrideInstructions) => {
    if (!assessmentData || !patientData) {
      setError('Dados necessários não encontrados');
      return;
    }
    const instructions = typeof overrideInstructions === 'string' ? overrideInstructions : specialInstructions;
    setGenerating(true);
    setError(null);
    try {
      await treatmentPlans.generate({
        patientId,
        assessmentId: assessmentData.id,
        specialInstructions: instructions,
      });
      await fetchAllPlans();
    } catch (err) {
      console.error('Error generating treatment plan:', err);
      setError(err.message || 'Erro ao gerar plano de tratamento');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateDefault = () => {
    setSpecialInstructions('');
    handleGenerateTreatmentPlan('');
  };

  const handlePrint = () => window.print();

  // --- Edit mode ---
  const enterEditMode = () => {
    if (!treatmentPlan) return;
    const parsed = parseMarkdownSections(treatmentPlan);
    setSections(parsed);
    setOriginalContent(treatmentPlan);
    setIsEditMode(true);
    setEditingSectionIndex(null);
  };

  const cancelEditMode = () => {
    if (hasUnsavedChanges) {
      if (!window.confirm('Descartar alterações não salvas?')) return;
    }
    setIsEditMode(false);
    setSections([]);
    setOriginalContent(null);
    setEditingSectionIndex(null);
  };

  const handleSave = async () => {
    if (!selectedPlan) return;
    setSaving(true);
    setError(null);
    try {
      const newContent = reconstructMarkdown(sections);
      await treatmentPlans.update(selectedPlan.id, { content: newContent });
      // Update local state so the plan reflects the save
      setAllPlans((prev) =>
        prev.map((p, i) => (i === selectedPlanIndex ? { ...p, content: newContent } : p))
      );
      setOriginalContent(newContent);
      setIsEditMode(false);
      setEditingSectionIndex(null);
    } catch (err) {
      console.error('Error saving treatment plan:', err);
      setError('Erro ao salvar plano de tratamento');
    } finally {
      setSaving(false);
    }
  };

  // Section editing callbacks
  const handleStartEdit = (index) => setEditingSectionIndex(index);
  const handleCloseEdit = () => setEditingSectionIndex(null);
  const handleContentChange = (index, newContent) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, content: newContent } : s)));
  };
  const handleDeleteSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
    if (editingSectionIndex === index) setEditingSectionIndex(null);
  };
  const handleAddSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      heading: 'Nova Seção',
      headingLevel: 2,
      content: '## Nova Seção\n\nConteúdo aqui...',
    };
    setSections((prev) => [...prev, newSection]);
    setEditingSectionIndex(sections.length);
  };

  // Signoff auto-save (debounced with status feedback)
  const selectedPlanIdRef = React.useRef(null);
  selectedPlanIdRef.current = selectedPlan?.id || null;

  const handleSignoffsChange = useCallback((newSignoffs) => {
    const planId = selectedPlanIdRef.current;
    if (!planId) return;
    // Update local state immediately
    setAllPlans((prev) =>
      prev.map((p) => (p.id === planId ? { ...p, signoffs: newSignoffs } : p))
    );
    // Debounced save to backend
    if (signoffSaveTimer.current) clearTimeout(signoffSaveTimer.current);
    setSignoffSaveStatus('saving');
    signoffSaveTimer.current = setTimeout(async () => {
      try {
        await treatmentPlans.update(planId, { signoffs: newSignoffs });
        setSignoffSaveStatus('saved');
        setTimeout(() => setSignoffSaveStatus(null), 2000);
      } catch (err) {
        console.error('Error saving signoffs:', err);
        setSignoffSaveStatus('error');
      }
    }, 800);
  }, []);

  const latestPlanHasInstructions = allPlans.length > 0 && !!allPlans[0].specialInstructions;

  // --- No assessment guard ---
  if (!assessmentData) {
    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-brand-700 to-brand-600 rounded-xl p-6 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <ClipboardList className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white m-0">Plano de Tratamento</h3>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="text-amber-800 text-sm">
            Uma avaliação é necessária para gerar o plano de tratamento. Faça o upload de uma avaliação na aba de avaliações primeiro.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generation Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-brand-700 to-brand-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 rounded-lg p-2">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white m-0">Gerador de Plano de Tratamento</h3>
                <p className="text-brand-100 text-xs mt-0.5">Baseado na avaliação do paciente</p>
              </div>
            </div>
            {treatmentPlan && (
              <div className="print:hidden flex items-center gap-2">
                {!isEditMode && (
                  <button
                    onClick={enterEditMode}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-sm rounded-lg transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    Editar
                  </button>
                )}
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-sm rounded-lg transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Special Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              Instruções Especiais
            </label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Insira aqui instruções específicas que devem ser consideradas no plano de tratamento (ex: limitações físicas, preferências, objetivos específicos, contexto familiar, etc.)"
              rows={3}
              className="w-full border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 p-3 text-sm text-gray-700 placeholder-gray-400 resize-none transition-shadow"
            />
          </div>

          {/* Generate Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => handleGenerateTreatmentPlan()}
              disabled={generating || !patientData}
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-gradient-to-r from-brand-700 to-brand-600 hover:from-brand-800 hover:to-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Gerando Plano...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {allPlans.length > 0 ? 'Gerar Plano' : 'Gerar Plano de Tratamento'}
                </>
              )}
            </button>

            {latestPlanHasInstructions && !generating && (
              <button
                onClick={handleGenerateDefault}
                disabled={!patientData}
                className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-brand-300 text-sm font-semibold rounded-lg text-brand-700 bg-white hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
              >
                <ClipboardList className="h-4 w-4" />
                Gerar Plano Padrão
              </button>
            )}

            {generating && (
              <div className="flex-1 max-w-xs">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Analisando avaliação...</span>
                  <span>{Math.round(generationProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-brand-600 to-brand-500 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${generationProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Version Selector */}
          {allPlans.length > 1 && (
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-gray-400" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Versões</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allPlans.map((plan, index) => {
                  const isActive = index === selectedPlanIndex;
                  const hasInstructions = !!plan.specialInstructions;
                  return (
                    <button
                      key={plan.id}
                      onClick={() => handleSelectPlan(index)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive
                          ? 'bg-brand-100 text-brand-800 ring-2 ring-brand-500 ring-offset-1'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span>v{plan.version}</span>
                      <span className="text-gray-400">—</span>
                      <span>{hasInstructions ? 'Personalizado' : 'Padrão'}</span>
                      {hasInstructions && (
                        <Lightbulb className={`h-3 w-3 ${isActive ? 'text-amber-500' : 'text-gray-400'}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Treatment Plan Result */}
      {treatmentPlan && (
        <div className={`treatment-plan-content bg-white rounded-xl shadow-sm overflow-hidden ${
          isEditMode
            ? 'border-2 border-brand-400 ring-2 ring-brand-200'
            : 'border border-gray-200'
        }`}>
          {/* Print-only header */}
          <div className="hidden print:block px-6 pt-6 pb-2 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900 m-0">Plano de Tratamento</h1>
            {patientData && (
              <p className="text-sm text-gray-600 mt-1">
                Paciente: {patientData.firstName} {patientData.lastName}
                {' — '}Versão {selectedPlan.version} ({selectedPlan.specialInstructions ? 'Personalizado' : 'Padrão'})
                {treatmentPlanGeneratedAt && ` — ${new Date(treatmentPlanGeneratedAt).toLocaleDateString('pt-BR')}`}
              </p>
            )}
          </div>

          {/* Plan header (screen only) */}
          <div className={`border-b px-6 py-3 flex items-center justify-between print:hidden ${
            isEditMode ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-100'
          }`}>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {isEditMode ? (
                <>
                  <Edit className="h-3.5 w-3.5 text-brand-500" />
                  <span className="text-brand-700 font-medium">Modo de edição</span>
                  {hasUnsavedChanges && (
                    <span className="ml-2 text-amber-600 font-medium">• Alterações não salvas</span>
                  )}
                </>
              ) : (
                <>
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  <span>
                    Plano v{selectedPlan.version} — {selectedPlan.specialInstructions ? 'Personalizado' : 'Padrão'}
                    {treatmentPlanGeneratedAt && ` — ${new Date(treatmentPlanGeneratedAt).toLocaleDateString('pt-BR')} às ${new Date(treatmentPlanGeneratedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </>
              )}
            </div>

            {isEditMode && (
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelEditMode}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Salvar
                </button>
              </div>
            )}
          </div>

          {/* Plan body */}
          <div className="p-6 md:p-8">
            {isEditMode ? (
              <TreatmentPlanEditor
                sections={sections}
                editingSectionIndex={editingSectionIndex}
                onStartEdit={handleStartEdit}
                onCloseEdit={handleCloseEdit}
                onContentChange={handleContentChange}
                onDeleteSection={handleDeleteSection}
                onAddSection={handleAddSection}
              />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={treatmentPlanComponents}
              >
                {treatmentPlan}
              </ReactMarkdown>
            )}

            {/* Signoff section */}
            <TreatmentPlanSignoff
              signoffs={selectedPlan?.signoffs || []}
              onSignoffsChange={handleSignoffsChange}
              readOnly={false}
              saveStatus={signoffSaveStatus}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentPlanTab;
